import { EltType } from 'legacy/lut/classes/models/partner360/ELT/EltType'
import { IELTData } from 'legacy/lut/classes/models/partner360/ELT/IELTData'
import { IWWPartners } from 'legacy/lut/classes/models/partner360/IWWPartners'
import { SizedTitle } from 'legacy/lut/components/widgets/public/partner360/ELT/ui/styledComponents/title/SizedTitle'
import { TitleWrapper } from 'legacy/lut/components/widgets/public/partner360/ELT/ui/styledComponents/title/titleWrapper/TitleWrapper'
import { Table } from 'legacy/lut/library/list'
import { useCallback, useMemo, useState } from 'react'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations/components/translation'
import { getUrlParams } from 'core-components/helpers/url'
import { Button } from 'core-components/library/button/Button'
import { Flex } from 'core-components/library/flex'
import { LoaderOverlay } from 'core-components/library/loader'

import { getFormedEltTranslation } from '../../../helpers/getFormedEltTranslation'
import { IPartnerGroupAdditionalInfo } from '../../../models/IPartnerGroupAdditionalInfo'
import { Alert } from '../../styledComponents/alert/Alert'
import { StyledWrapper } from '../../styledComponents/styledWrapper/StyledWrapper'
import { AddNewVersionDialog } from '../addNewVersionDialog/AddNewVersionDialog'
import { IAddNewVersion } from '../addNewVersionDialog/models'
import { CloneVersionDialog } from '../cloneVersionDialog/CloneVersionDialog'
import { DeleteVersionDialog } from '../deleteVersionDialog/DeleteVersionDialog'
import { useVersionsTableData } from '../hooks/useVersionsSource'
import { TSelectVersionMode } from '../types/Types'
import { UpdateVersionDialog } from '../updateVersionDialog/UpdateVersionDialog'

import styles from './VersionsHandler.module.scss'

interface IProps {
  selectVersion: (
    mode: TSelectVersionMode
  ) => (version: IELTData, versionName?: string, versionSerialNumber?: number) => void
  wip: boolean
  addNewVersion: (args: IAddNewVersion) => void
  partnerId: string
  partnerGroupInfo: IPartnerGroupAdditionalInfo
  partnerData: IWWPartners | null
}

export const VersionsHandler = ({
  selectVersion,
  wip,
  addNewVersion,
  partnerGroupInfo,
  partnerId,
  partnerData,
}: IProps) => {
  const [showAddNewVersionDialog, setShowAddNewVersionDialog] = useState(false)
  const [selectedVersionToClone, setSelectedVersionToClone] = useState<IELTData>()
  const [itemToDelete, setItemToDelete] = useState<IELTData>()
  const [itemToUpdate, setItemToUpdate] = useState<IELTData>()
  const [eltTypeToCreate, setEltTypeToCreate] = useState<EltType>(EltType.Pptx)
  const { versionsSource, getColumns } = useVersionsTableData()

  const columns = getColumns({
    selectVersion,
    setSelectedVersionToClone,
    setItemToDelete,
    setItemToUpdate,
  })

  const titleText = useMemo(() => {
    if (partnerGroupInfo.groupName) {
      return partnerGroupInfo.groupName
    }

    return `${partnerData?.CommunicationName} ${partnerId}`
  }, [partnerGroupInfo, partnerData?.CommunicationName, partnerId])

  const isGlobalPartner = useMemo(() => {
    const urlParams = getUrlParams()
    return 'vgp' in urlParams
  }, [])

  const showCreateVersionDialog = useCallback(
    (eltType: EltType) => () => {
      setEltTypeToCreate(eltType)
      setShowAddNewVersionDialog(true)
    },
    []
  )
  return (
    <StyledWrapper>
      <LoaderOverlay show={wip} />
      <TitleWrapper variant={'withBackground'} align={'center'} className={styles.title}>
        <SizedTitle size={'h3'} align={'center'} variant={'withBackground'}>
          <Flex alignItems={'baseline'} gap={'sm'}>
            <RenderWhen condition={isGlobalPartner}>
              <i className={'kbase kbase-globe'} />
            </RenderWhen>
            {titleText}
          </Flex>
        </SizedTitle>
      </TitleWrapper>

      <Alert>
        <div className={styles.title}>
          <Translation text={getFormedEltTranslation('exists-several-versions')} />{' '}
        </div>
      </Alert>
      <Flex gap={'md'}>
        <Button className={styles.addNewVersion} onClick={showCreateVersionDialog(EltType.Pptx)}>
          <Translation text={getFormedEltTranslation('add-new-version')} />
        </Button>
        <Button
          className={styles.addNewVersion}
          onClick={showCreateVersionDialog(EltType.DocJanuary26)}
        >
          <Translation text={getFormedEltTranslation('add-new-version-doc')} />
        </Button>
      </Flex>
      <Table<IELTData>
        rowsPerPageOptions={[{ key: 10, value: '10' }]}
        hasTranslate
        header
        pagination
        source={versionsSource}
        columns={columns}
      />
      <CloneVersionDialog
        selectedVersion={selectedVersionToClone || ({} as IELTData)}
        show={Boolean(selectedVersionToClone)}
        saveHandler={selectVersion('clone')}
        onClose={() => setSelectedVersionToClone(undefined)}
      />
      <UpdateVersionDialog
        selectedVersion={itemToUpdate || ({} as IELTData)}
        show={Boolean(itemToUpdate)}
        closeDialog={() => setItemToUpdate(undefined)}
      />
      <DeleteVersionDialog
        selectedVersion={itemToDelete || ({} as IELTData)}
        show={Boolean(itemToDelete)}
        closeDialog={() => {
          setItemToDelete(undefined)
        }}
      />
      <AddNewVersionDialog
        show={showAddNewVersionDialog}
        partnerGroupInfo={partnerGroupInfo}
        partnerId={partnerId}
        eltType={eltTypeToCreate}
        addNewVersion={addNewVersion}
        wip={wip}
        onClose={() => setShowAddNewVersionDialog(false)}
      />
    </StyledWrapper>
  )
}
